import React, { Fragment, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { MiningEnergy } from "../../models/miningenergy.model";
import { selectMenuStates } from "../../store/reducers/menuSlice";
import { getMiningEnergySum } from "../../api/getMiningEnergySum";
import { Group } from "../../models/group.model";
import ModifyEnergyCostModal from "./miningenergy/modals/ModifyMiningMachineModal";
import RemoveEnergyCostModal from "./miningenergy/modals/RemoveEnergyCostModal";

type EnergyCostProps = {
    group: Group | undefined;
}

export default function MiningEnergySummarizerPanel({ group }: EnergyCostProps) {

    const menu = useSelector(selectMenuStates);
    const navigate = useNavigate();

    const [records, setRecords] = React.useState<MiningEnergy[]>([]);
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [showModifyModal, setShowModifyModal] = React.useState<boolean>(false);
    const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);
    const [selectedRecord, setSelectedRecord] = React.useState<MiningEnergy>();
    const [recordStateChange, setRecordStateChange] = React.useState(false);

    const handleStateChange = () => setRecordStateChange(!recordStateChange);

    React.useEffect(() => {
        if (!group) return;

        getMiningEnergySum(group?.group_id)
            .then((response) => {
                // sort records by date DESC
                const sorted = response.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                setRecords(sorted);
            })
            .catch((error) => {
                console.error(error);
            });

    }, [recordStateChange, group]);

    const closeModal = () => {
        setShowRemoveModal(false);
        setShowModifyModal(false);
        setShowModal(false);
    }

    const openModal = () => setShowModal(true);

    const openRemoveModal = (record: MiningEnergy) => {
        setShowRemoveModal(true);
        setSelectedRecord(record);
    }

    const openModifyModal = (record: MiningEnergy) => {
        setShowModifyModal(true);
        setSelectedRecord(record);
    }

    return (
        <>
            <ModifyEnergyCostModal
                group_id={menu?.daisyMenu?.group_id}
                title={"Energiaköltségek módosítása"}
                showModal={showModifyModal}
                closeModal={closeModal}
                handleStateChange={handleStateChange}
                miningMachine={undefined}
                globalHandling={true}
                row={selectedRecord}
            />

            <RemoveEnergyCostModal
                title={"Energiaköltségek törlése"}
                group_id={menu?.daisyMenu?.group_id}
                showModal={showRemoveModal}
                closeModal={closeModal}
                handleStateChange={handleStateChange}
                globalHandling={true}
                row={selectedRecord}
            />

            {records.length === 0 ? (
                <p className="text-center mt-5">
                    A(z) <span className="text-brown font-semibold">{group?.group_name}</span> energia befizetései jelenleg még nincsenek rögzítve!
                </p>
            ) : (
                <Fragment>
                    <h2 className="mt-6 mb-4 text-2xl text-center font-bold text-brown">
                        A(z) <span className="font-semibold">{group?.group_name}</span> energia befizetései:
                    </h2>

                    <div className="flex justify-center border rounded-lg shadow-md bg-dark-blue p-4 m-3 mx-auto">
                        <table className="min-w-full table-auto divide-y divide-gray-600">
                            <thead className="bg-dark-blue text-cream">
                                <tr>
                                    <th className="py-3 px-6 text-xs font-semibold tracking-wider text-left uppercase">Dátum (Kulcs)</th>
                                    <th className="py-3 px-6 text-xs font-semibold tracking-wider text-left uppercase">KW/h</th>
                                    <th className="py-3 px-6 text-xs font-semibold tracking-wider text-left uppercase">Ár</th>
                                    <th className="py-3 px-6 text-xs font-semibold tracking-wider text-left uppercase">Fizette</th>
                                    <th className="py-3 px-6 text-xs font-semibold tracking-wider text-center uppercase" colSpan={2}>Műveletek</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                            {records.map((record, index) => (
                                <tr
                                    key={index}
                                    className={`hover:bg-gray-800 transition-colors ${
                                        record.investmentLeftover ? "bg-yellow-900/40" : ""
                                    }`}
                                >
                                    <td className="py-3 px-6 text-sm font-medium whitespace-nowrap text-white">
                                        {record.date.toString().split("T")[0]}
                                        {record.investmentLeftover && (
                                            <span className="ml-2 text-xs text-yellow-400 font-semibold">
                                                [A befizetés része]
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-3 px-6 text-sm whitespace-nowrap text-cream">
                                        {record.kwh}
                                    </td>
                                    <td className="py-3 px-6 text-sm whitespace-nowrap text-white">
                                        {record.price}
                                    </td>
                                    <td className="py-3 px-6 text-sm whitespace-nowrap text-white">
                                        {record.paidby
                                            ? `Ügyfél fizette: ${
                                                record.MiningEnergyPayer
                                                    ? "[" + record.MiningEnergyPayer.user.name + "]"
                                                    : "Minden ügyfél arányosan"
                                            }`
                                            : `Human D.C. ${
                                                record.MiningEnergyPayer
                                                    ? "[" + record.MiningEnergyPayer.user.name + " részére]"
                                                    : "a cég saját részére"
                                            }`}
                                    </td>
                                    <td
                                        onClick={() => openModifyModal(record)}
                                        className="text-center"
                                    >
                                        <p className="py-2 px-4 text-sm font-medium text-cream hover:cursor-pointer hover:underline">
                                            Módosít
                                        </p>
                                    </td>
                                    <td
                                        onClick={() => openRemoveModal(record)}
                                        className="text-center"
                                    >
                                        <p className="py-2 px-4 text-sm font-medium text-error hover:cursor-pointer hover:underline">
                                            Törlés
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>

                        </table>
                    </div>
                </Fragment>
            )}
        </>
    );
}
